import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useScreenRenderContext, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef } from '@micromag/core/contexts';
import { useDimensionObserver, useTrackScreenEvent } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout, { Spacer } from '@micromag/element-layout';
import ShareOptions from '@micromag/element-share-options';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { Heading1, Text, Container as Container$1 } from '@micromag/transforms/apple-news';

var styles = {"container":"micromag-screen-share-container","background":"micromag-screen-share-background","disabled":"micromag-screen-share-disabled","hidden":"micromag-screen-share-hidden","placeholder":"micromag-screen-share-placeholder","content":"micromag-screen-share-content","emptyHeading":"micromag-screen-share-emptyHeading","emptyOptions":"micromag-screen-share-emptyOptions","heading":"micromag-screen-share-heading","layout":"micromag-screen-share-layout","shareOptions":"micromag-screen-share-shareOptions","isCentered":"micromag-screen-share-isCentered","shareButton":"micromag-screen-share-shareButton","header":"micromag-screen-share-header","footer":"micromag-screen-share-footer"};

var propTypes = {
  layout: PropTypes.oneOf(['top', 'middle', 'bottom']),
  heading: PropTypes$1.headingElement,
  shareUrl: PropTypes.string,
  options: PropTypes.objectOf(PropTypes.bool),
  buttonsStyle: PropTypes$1.boxStyle,
  buttonsTextStyle: PropTypes$1.textStyle,
  centered: PropTypes.bool,
  spacing: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  id: PropTypes.string,
  index: PropTypes.number,
  current: PropTypes.bool,
  active: PropTypes.bool,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'top',
  heading: null,
  shareUrl: null,
  options: null,
  buttonsStyle: null,
  buttonsTextStyle: null,
  centered: false,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  id: null,
  index: null,
  current: true,
  active: true,
  className: null
};
var ShareScreen = function ShareScreen(_ref) {
  var layout = _ref.layout,
    heading = _ref.heading,
    shareUrl = _ref.shareUrl,
    options = _ref.options,
    buttonsStyle = _ref.buttonsStyle,
    buttonsTextStyle = _ref.buttonsTextStyle,
    centered = _ref.centered,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    id = _ref.id,
    index = _ref.index,
    current = _ref.current,
    active = _ref.active,
    className = _ref.className;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var backgroundPlaying = current && (isView || isEdit);
  var backgroundShouldLoad = current || active;
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var _useDimensionObserver = useDimensionObserver(),
    headerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.height,
    headerHeight = _useDimensionObserver2 === void 0 ? 0 : _useDimensionObserver2;
  var _useDimensionObserver3 = useDimensionObserver(),
    footerRef = _useDimensionObserver3.ref,
    _useDimensionObserver4 = _useDimensionObserver3.height,
    footerHeight = _useDimensionObserver4 === void 0 ? 0 : _useDimensionObserver4;
  var currentUrl = useMemo(function () {
    if (typeof window === 'undefined') return '';
    var _ref2 = window.location || {},
      _ref2$hostname = _ref2.hostname,
      hostname = _ref2$hostname === void 0 ? null : _ref2$hostname,
      _ref2$pathname = _ref2.pathname,
      pathname = _ref2$pathname === void 0 ? null : _ref2$pathname;
    var parts = pathname.split('/');
    /**
     * for the last portion of the path, if it's equal to the screen index,
     * or the screen id, then don't include it in the share URL.
     * This makes sure we're not doing a `.replace()` that might remove a part
     * from the slug of the current Micromag.
     * (e.g. if the url is `/10-reasons-to-lorem-ipsum` and the share screen
     * is on screen 10, then a string replace would remove the `/10` from the
     * URL)
     */
    return parts.reduce(function (acc, part, i) {
      return (
        // it's equal to the screen index, or equal to the screen ID, or it's empty
        i === parts.length - 1 && parseInt(part, 10) === index || part === id || part === '' ? acc : "".concat(acc, "/").concat(part)
      );
    }, hostname);
  }, [index, id]);

  // if not share URl was specified, default to the currentURL (without the screen index/id part)
  var finalShareURL = shareUrl || currentUrl;
  var defaultOptions = options !== null ? ['email', 'facebook', 'twitter', 'linkedin', 'whatsapp'] : [];
  var selectedOptions = options !== null ? Object.keys(options).reduce(function (acc, key) {
    if (!options[key]) return acc;
    return [].concat(_toConsumableArray(acc), [key]);
  }, []) : defaultOptions;
  var trackingEnabled = isView;
  var trackEvent = useTrackScreenEvent('share');
  var onClickShare = useCallback(function (type) {
    if (trackingEnabled) {
      trackEvent('click_share', type, {
        shareUrl: finalShareURL
      });
    }
  }, [trackEvent]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": true
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    verticalAlign: layout,
    fullscreen: true,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (!isPreview ? viewerTopHeight : 0) + (headerHeight || spacing),
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + (footerHeight || spacing)
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    className: styles.header,
    ref: headerRef,
    style: {
      paddingTop: spacing / 2,
      paddingBottom: spacing,
      paddingLeft: spacing,
      paddingRight: spacing,
      transform: "translate(0px, ".concat(viewerTopHeight, "px)")
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement(ScreenElement, {
    key: "title",
    placeholder: "title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "X7kRRa",
      defaultMessage: [{
        "type": 0,
        "value": "Heading"
      }]
    }),
    emptyClassName: styles.emptyHeading,
    isEmpty: !heading || (heading === null || heading === void 0 ? void 0 : heading.body) === ''
  }, heading ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: classNames([styles.heading])
  }, heading)) : null), /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer",
    size: 5
  }), /*#__PURE__*/React.createElement(ScreenElement, {
    key: "share-options",
    placeholder: "share-options",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "RucIeL",
      defaultMessage: [{
        "type": 0,
        "value": "Share options"
      }]
    }),
    emptyClassName: styles.emptyOptions,
    isEmpty: !options
  }, /*#__PURE__*/React.createElement(ShareOptions, {
    className: classNames([styles.shareOptions, _defineProperty({}, styles.isCentered, centered)]),
    buttonClassName: styles.shareButton,
    url: finalShareURL,
    options: selectedOptions,
    onShare: onClickShare,
    buttonsStyle: buttonsStyle,
    buttonsTextStyle: buttonsTextStyle
  })), !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    className: styles.footer,
    ref: footerRef,
    style: {
      paddingTop: spacing,
      paddingBottom: spacing / 2,
      paddingLeft: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingRight: Math.max(viewerBottomSidesWidth - spacing, 0)
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: backgroundShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
ShareScreen.propTypes = propTypes;
ShareScreen.defaultProps = defaultProps;

var transform = function transform(newStory, _ref) {
  var text = _ref.text,
    title = _ref.title;
  var _Heading = Heading1(newStory, title),
    titleStory = _Heading.story,
    titleComponent = _Heading.component;
  var _Text = Text(titleStory, text),
    textStory = _Text.story,
    textComponent = _Text.component;
  var _Container = Container$1(textStory, [].concat(_toConsumableArray(titleComponent ? [titleComponent] : []), _toConsumableArray(textComponent ? [textComponent] : []))),
    containerStory = _Container.story,
    containerComponent = _Container.component;
  return _objectSpread(_objectSpread({}, containerStory), {}, {
    components: [].concat(_toConsumableArray(newStory.components || []), _toConsumableArray(containerComponent ? [containerComponent] : []))
  });
};

// eslint-disable-next-line

var transforms = /*#__PURE__*/Object.freeze({
    __proto__: null,
    appleNews: transform
});

var definition = {
  id: 'share',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "64JlRq",
      defaultMessage: [{
        "type": 0,
        "value": "Share"
      }]
    }),
    order: 12
  },
  title: defineMessage({
    id: "5mk1b+",
    defaultMessage: [{
      "type": 0,
      "value": "Share"
    }]
  }),
  component: ShareScreen,
  layouts: ['top', 'middle', 'bottom'],
  transforms: transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'top',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'shareUrl',
    type: 'url',
    label: defineMessage({
      id: "0R+nB5",
      defaultMessage: [{
        "type": 0,
        "value": "Custom URL"
      }]
    }),
    help: defineMessage({
      id: "bQsPrs",
      defaultMessage: [{
        "type": 0,
        "value": "If this field is left empty, this Micromag's URL will be shared."
      }]
    })
  }, {
    name: 'heading',
    type: 'heading-element',
    theme: {
      textStyle: 'heading2'
    },
    label: defineMessage({
      id: "wx5PI0",
      defaultMessage: [{
        "type": 0,
        "value": "Heading"
      }]
    })
  }, {
    name: 'options',
    type: 'fields',
    isList: true,
    label: defineMessage({
      id: "4js1kW",
      defaultMessage: [{
        "type": 0,
        "value": "Share Options"
      }]
    }),
    fields: [{
      name: 'copylink',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "rKuiKj",
        defaultMessage: [{
          "type": 0,
          "value": "Copy link"
        }]
      })
    }, {
      name: 'facebook',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "r+bcpa",
        defaultMessage: [{
          "type": 0,
          "value": "Facebook"
        }]
      })
    }, {
      name: 'twitter',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "mlucL+",
        defaultMessage: [{
          "type": 0,
          "value": "Twitter"
        }]
      })
    }, {
      name: 'linkedin',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "hvxkzE",
        defaultMessage: [{
          "type": 0,
          "value": "LinkedIn"
        }]
      })
    }, {
      name: 'whatsapp',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "3B9cb3",
        defaultMessage: [{
          "type": 0,
          "value": "Whatsapp"
        }]
      })
    },
    // {
    //     name: 'facebookMessenger',
    //     type: 'toggle',
    //     defaultValue: true,
    //     label: defineMessage({
    //         defaultMessage: 'Facebook Messenger',
    //         description: 'Field label',
    //     }),
    // },
    {
      name: 'email',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "LI0Gz4",
        defaultMessage: [{
          "type": 0,
          "value": "Email"
        }]
      })
    }, {
      name: 'sms',
      type: 'toggle',
      defaultValue: true,
      label: defineMessage({
        id: "d3va12",
        defaultMessage: [{
          "type": 0,
          "value": "SMS"
        }]
      })
    }]
  }, {
    name: 'buttonsTextStyle',
    type: 'text-style-form',
    label: defineMessage({
      id: "gwPu/I",
      defaultMessage: [{
        "type": 0,
        "value": "Buttons text"
      }]
    })
  }, {
    name: 'buttonsStyle',
    type: 'box-style-form',
    label: defineMessage({
      id: "l5uLMd",
      defaultMessage: [{
        "type": 0,
        "value": "Buttons"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { ShareScreen, definition as default };
